<script setup lang="ts">
import type { PlpGridItemWithType } from '@design-system/components/Plp/FunctionalBar/PlpFunctionalBar.props'
import type { ProductCardHorizontal } from '@integration-layer/modules/05.productServices/runtime/components/WrapperProductTile.props'
import type { PdpAlsoLikeGridProps } from './PdpAlsoLikeGrid.props'
import { resolveBrandDeliveryKey } from '@integration-layer/utils/resolveBrandDeliveryKey'

const props = withDefaults(defineProps<PdpAlsoLikeGridProps>(), {
  initialTrackedCount: 10,
})
const { breakpoint } = useProjectBreakpoint()
// `smallerOrEqual` (not `isSmallerOrEqual`) returns a ref: the grid needs to
// react to resizes, and on SSR the non-reactive variant never corrects itself.
const isMobile = breakpoint.smallerOrEqual('md')

const emit = defineEmits<{
  openQuickBuy: [product: ProductCardHorizontal]
  loadMore: []
  emitGaLoadMoreEvent: []
}>()

const { currentWebsite, isEmporioArmaniWebsite, isEa7Website } = useWebsite()
const { ts } = useI18n()

/** Desktop columns for the also-like grid (matches PLP gridWall). */
const ITEMS_PER_ROW = 6

/** Products shown per "show more" page (client-side; XGen returns the full list at once). */
const pageSize = computed(() => (isMobile.value ? 12 : 36))

/**
 * Row index after which the editorial banner is inserted.
 * TODO: read from Amplience when the dedicated PDP editorial schema is ready.
 */
const EDITORIAL_ROW_POSITION = 4

const GRID_CONFIGURATION = {
  name: 'pdpAlsoLike',
  icon: {},
  class: 'grid-cols-3 lg:grid-cols-6',
} satisfies PlpGridItemWithType

const bannerDeliveryKey = computed(() =>
  resolveBrandDeliveryKey('pdp-also-like-banner', currentWebsite.value)
)

const visibleCount = ref(pageSize.value)

/**
 * The breakpoint only resolves on the client, so the first page size lands after
 * hydration. Follow it until the visitor pages manually, then leave it alone.
 */
const hasPagedManually = ref(false)

watch(pageSize, size => {
  if (!hasPagedManually.value) visibleCount.value = size
})

/**
 * The grid shows a whole page at once — more than the wrapper tracked on mount
 * — so ask it to dispatch `view_item_list` for the full list. It dispatches
 * everything, so once is enough.
 */
const hasRequestedFullTracking = ref(false)

watchEffect(() => {
  if (hasRequestedFullTracking.value || !props.products.length) return

  const shownProducts = Math.min(visibleCount.value, props.products.length)
  if (shownProducts <= props.initialTrackedCount) return

  hasRequestedFullTracking.value = true
  emit('loadMore')
})

watch(
  () => props.products.length,
  () => {
    visibleCount.value = pageSize.value
    hasRequestedFullTracking.value = false
    hasPagedManually.value = false
  }
)

type GridItem =
  | {
      type: 'product'
      key: string
      content: PdpAlsoLikeGridProps['products'][number]
    }
  | {
      type: 'editorial'
      key: string
    }

const allItems = computed<GridItem[]>(() => {
  const list: GridItem[] = props.products.map((content, index) => ({
    type: 'product',
    key: `pdp-also-like-${index}-${content.objectID}`,
    content,
  }))

  // The banner is desktop-only. Leave it out of the list entirely rather than
  // hiding it: EditorialModulesWrapper fetches the CMS on mount, so a mounted
  // but hidden banner would still cost a request on mobile.
  if (!bannerDeliveryKey.value || isMobile.value) return list

  // splice beyond length appends the banner when there are fewer products
  list.splice(EDITORIAL_ROW_POSITION * ITEMS_PER_ROW, 0, {
    type: 'editorial',
    key: 'pdp-also-like-banner',
  })

  return list
})

/** Visible slice: PAGE_SIZE counts products only; editorial is included when reached. */
const items = computed<GridItem[]>(() => {
  const out: GridItem[] = []
  let shownProducts = 0

  for (const item of allItems.value) {
    if (item.type === 'product') {
      if (shownProducts >= visibleCount.value) break
      shownProducts++
    }
    out.push(item)
  }

  return out
})

const hasMore = computed(() => visibleCount.value < props.products.length)

const loadMore = () => {
  emit('emitGaLoadMoreEvent')
  hasPagedManually.value = true
  visibleCount.value = Math.min(
    visibleCount.value + pageSize.value,
    props.products.length
  )
}

const tileImageSizes = PLP_PRODUCT_TILE_SIZES
</script>

<template>
  <!-- Break out of PdpRelatedProducts / grid-standard horizontal padding -->
  <div class="-mx-[var(--padding-x)]">
    <PlpGrid
      :id="`XSE-${predictionId}`"
      :grid-configuration="GRID_CONFIGURATION"
      class="XGen_SmartElement"
    >
      <template v-for="item in items" :key="item.key">
        <li
          v-if="item.type === 'editorial'"
          class="col-span-full [&:not(:has(.contents>*))]:hidden"
        >
          <EditorialModulesWrapper :delivery-key="bannerDeliveryKey" />
        </li>
        <li v-else-if="item.type === 'product'" class="col-span-1">
          <WrapperProductTile :product="item.content">
            <template #default="{ product, productCardHorizontal }">
              <UIProductTile
                xse-type="rec"
                :xse-prod-code="product.sku"
                :disable-swipe-mobile="true"
                :image-sizes="tileImageSizes"
                :hide-for-customers="hideForCustomers"
                :only-img="true"
                :product="{
                  ...product,
                  linkToProduct: `${product.linkToProduct}?xse_prod_code=${product.sku}&xse=${predictionId}`,
                }"
                @open-quick-buy="
                  () =>
                    productCardHorizontal &&
                    emit('openQuickBuy', productCardHorizontal)
                "
              />
            </template>
          </WrapperProductTile>
        </li>
      </template>
    </PlpGrid>

    <div
      v-if="hasMore"
      class="flex flex-col items-center justify-center gap-2 pb-5 pt-10 lg:pt-6"
    >
      <button
        type="button"
        class="cta cta-l text-button-2 w-[247px] min-w-max md:w-[218px]"
        :class="[
          {
            'cta-primary': !isEmporioArmaniWebsite || !isEa7Website,
            'cta-secondary': isEmporioArmaniWebsite || isEa7Website,
          },
        ]"
        @click="loadMore"
      >
        {{ `${ts('plp.showMore')} (${visibleCount}/${products.length})` }}
      </button>
    </div>
  </div>
</template>
